import React, { useCallback, useEffect, useMemo, useState } from "react";
import { apiGetAll, getTimeCodes, getApprovalQueue, approveBulk } from "../api";
import { mondayOf, weekStartsBetween } from "../utils/weekUtils";
import WeekView from "../components/timeentry/WeekView";
import { useStickyFlag } from "../hooks/useStickyFlag";
import CalendarMonth from "../components/timeentry/CalendarMonth";

const MONTHS = ["January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"];

function todayISO() {
  const d = new Date();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${m}-${day}`;
}

function shiftDays(iso, delta) {
  const [y, m, d] = iso.split("-").map(Number);
  const dt = new Date(y, m - 1, d);
  dt.setDate(dt.getDate() + delta);
  const mm = String(dt.getMonth() + 1).padStart(2, "0");
  const dd = String(dt.getDate()).padStart(2, "0");
  return `${dt.getFullYear()}-${mm}-${dd}`;
}

function monthBounds(iso) {
  const [y, m] = iso.split("-").map(Number);
  const first = `${y}-${String(m).padStart(2, "0")}-01`;
  const lastDay = new Date(y, m, 0).getDate();
  const last = `${y}-${String(m).padStart(2, "0")}-${String(lastDay).padStart(2, "0")}`;
  return [first, last];
}

// The console's period: one day, a whole week, or a whole month. Both the queue
// figures and the bulk-approve button are scoped to it. Day exists so a file can
// be closed out the afternoon it is finished (owner, 2026-10-27).
function periodRange(mode, anchor) {
  if (mode === "day") return [anchor, anchor];
  if (mode === "month") return monthBounds(anchor);
  const ws = mondayOf(anchor);
  return [ws, shiftDays(ws, 6)];
}

// Shift an anchor by one period (week -> +/-7 days, month -> +/-1 calendar month).
// Shared by the console's own Prev/Next and the drill-down's Prev/Next, which step
// two independent anchors by two independent periods (console `mode` vs drill `grain`).
function shiftAnchor(period, anchorVal, dir) {
  if (period === "day") return shiftDays(anchorVal, dir);
  if (period === "month") {
    const [y, m] = anchorVal.split("-").map(Number);
    const dt = new Date(y, m - 1 + dir, 1);
    return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-01`;
  }
  return shiftDays(mondayOf(anchorVal), dir * 7);
}

const WEEKDAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

function periodLabel(mode, anchor) {
  if (mode === "day") {
    const [y, m, d] = anchor.split("-").map(Number);
    return `${WEEKDAYS[new Date(y, m - 1, d).getDay()]}, ${MONTHS[m - 1]} ${d}, ${y}`;
  }
  if (mode === "month") {
    const [y, m] = anchor.split("-").map(Number);
    return `${MONTHS[m - 1]} ${y}`;
  }
  const [start, end] = periodRange("week", anchor);
  const [sy, sm, sd] = start.split("-").map(Number);
  const [ey, em, ed] = end.split("-").map(Number);
  if (sy === ey && sm === em) return `${MONTHS[sm - 1]} ${sd} to ${ed}, ${sy}`;
  if (sy === ey) return `${MONTHS[sm - 1]} ${sd} to ${MONTHS[em - 1]} ${ed}, ${sy}`;
  return `${MONTHS[sm - 1]} ${sd}, ${sy} to ${MONTHS[em - 1]} ${ed}, ${ey}`;
}

const btn = (active) => ({
  padding: "4px 12px", borderRadius: 6, cursor: "pointer", fontWeight: 600,
  border: "1px solid " + (active ? "#2563eb" : "#d1d5db"),
  background: active ? "#eff6ff" : "white",
  color: active ? "#2563eb" : "#374151",
});

// Access is gated by the nav (user.can_approve_time) and enforced server-side —
// every endpoint this page calls is approver-only — so the page takes no `user`.
export default function TimeApprovalPage() {
  const [tab, setTab] = useState("pending");     // "pending" | "approved"
  const [mode, setMode] = useState("week");      // "day" | "week" | "month" — the console period
  const [anchor, setAnchor] = useState(todayISO());
  const [queue, setQueue] = useState(null);
  const [queueError, setQueueError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [clients, setClients] = useState([]);
  const [timeCodes, setTimeCodes] = useState([]);
  const [busy, setBusy] = useState(false);
  const [banner, setBanner] = useState(null);

  // Drill-down: which employee (a USER id — TimeEntry.created_by is a User FK),
  // and at what grain. `drillAnchor` is the drill-down's OWN anchor — separate from
  // the console's `anchor` — so stepping Prev/Next while drilled in never disturbs
  // the queue period the approver was looking at.
  const [drill, setDrill] = useState(null);      // { employeeId, name }
  const [showEmptyDays, setShowEmptyDays] = useStickyFlag("te_show_empty_days", true);
  const [grain, setGrain] = useState("week");    // "day" | "week" | "month"
  const [dayISO, setDayISO] = useState(todayISO());
  const [drillAnchor, setDrillAnchor] = useState(todayISO());

  const [rangeStart, rangeEnd] = useMemo(() => periodRange(mode, anchor), [mode, anchor]);

  useEffect(() => {
    (async () => {
      try {
        const [cl, tc] = await Promise.all([apiGetAll("/api/clients/"), getTimeCodes()]);
        setClients(Array.isArray(cl) ? cl : cl.results || []);
        setTimeCodes((tc.results || tc).filter((t) => t.is_active));
      } catch (e) {
        setBanner({ type: "error", msg: "Failed to load clients and time codes. Please refresh." });
      }
    })();
  }, []);

  const loadQueue = useCallback(async () => {
    setLoading(true);
    setQueueError(null);
    try {
      const data = await getApprovalQueue({
        status: tab === "pending" ? "submitted" : "approved",
        start_date: rangeStart,
        end_date: rangeEnd,
      });
      setQueue(data);
    } catch (e) {
      setQueue(null);
      setQueueError("Failed to load the approval queue. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [tab, rangeStart, rangeEnd]);

  useEffect(() => { loadQueue(); }, [loadQueue]);

  // A stale banner ("Approved 12 entries.") must not survive a move to an unrelated
  // period, tab, or a drill in/out (drill identity changes on both ends of a drill).
  useEffect(() => { setBanner(null); }, [tab, mode, anchor, drill]);

  const stepPeriod = (dir) => setAnchor(shiftAnchor(mode, anchor, dir));

  // The drill-down steps by its OWN grain, not the console's mode (Fix 2) — a
  // month-grain drill must jump a month even if the console itself is in week mode.
  const stepDrillPeriod = (dir) =>
    setDrillAnchor(shiftAnchor(grain === "month" ? "month" : "week", drillAnchor, dir));

  // Requirement 6: bulk approve by day, week or month, across all employees at once.
  const onApproveAll = async () => {
    const label = mode === "day"
      ? `day ${periodLabel(mode, anchor)}`
      : `${mode === "month" ? "month" : "week"} of ${periodLabel(mode, anchor)}`;
    if (!window.confirm(
      `Approve all ${queue?.entry_count ?? 0} submitted entries for the ${label}, ` +
      `across all ${queue?.employees?.length ?? 0} employee(s)?`)) return;
    setBusy(true);
    try {
      const res = await approveBulk(rangeStart, rangeEnd, null); // null => everyone
      setBanner({ type: "ok", msg: `Approved ${res.count} entr${res.count === 1 ? "y" : "ies"}.` });
      await loadQueue();
    } catch (e) {
      setBanner({ type: "error", msg: "Bulk approve failed. Please try again." });
    } finally {
      setBusy(false);
    }
  };

  const drillWeekStarts = useMemo(() => {
    if (grain === "month") {
      const [first, last] = monthBounds(drillAnchor);
      return weekStartsBetween(first, last);
    }
    return [mondayOf(grain === "day" ? dayISO : drillAnchor)];
  }, [grain, drillAnchor, dayISO]);

  if (drill) {
    return (
      <div style={{ padding: "16px 24px" }}>
        <button onClick={() => { setDrill(null); loadQueue(); }}
          style={{ ...btn(false), marginBottom: 12 }}>‹ Back to queue</button>
        <h2 style={{ margin: "0 0 12px" }}>{drill.name}</h2>

        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
          <button style={btn(grain === "day")} onClick={() => {
            // Seed Day with a date inside the currently drilled span, unless
            // it's already showing a date within that span.
            const inRange = grain === "month"
              ? dayISO >= monthBounds(drillAnchor)[0] && dayISO <= monthBounds(drillAnchor)[1]
              : dayISO >= mondayOf(drillAnchor) && dayISO <= shiftDays(mondayOf(drillAnchor), 6);
            if (!inRange) setDayISO(drillAnchor);
            setGrain("day");
          }}>Day</button>
          <button style={btn(grain === "week")} onClick={() => setGrain("week")}>Week</button>
          <button style={btn(grain === "month")} onClick={() => setGrain("month")}>Month</button>
          {grain === "day" ? (
            <input type="date" value={dayISO}
              onChange={(e) => { setDayISO(e.target.value); setDrillAnchor(e.target.value); }} />
          ) : (
            <>
              <button onClick={() => stepDrillPeriod(-1)}>‹ Prev</button>
              <strong style={{ minWidth: 220, textAlign: "center" }}>
                {periodLabel(grain === "month" ? "month" : "week", drillAnchor)}
              </strong>
              <button onClick={() => stepDrillPeriod(1)}>Next ›</button>
            </>
          )}
          {grain === "week" && (
            // Off hides days with no time, which is what this grid used to do
            // unconditionally. On is the default: an approver should be able to
            // see that Tuesday was blank rather than wonder if it failed to load.
            <label style={{ marginLeft: "auto", display: "flex", alignItems: "center",
              gap: 6, fontSize: 13, color: "#374151" }}>
              <input type="checkbox" checked={showEmptyDays}
                onChange={(e) => setShowEmptyDays(e.target.checked)} />
              Show empty days
            </label>
          )}
        </div>

        {grain === "month" ? (
          <CalendarMonth weekStarts={drillWeekStarts} createdById={drill.employeeId}
            clients={clients} timeCodes={timeCodes} review />
        ) : grain === "day" ? (
          <WeekView weekStart={mondayOf(dayISO)} days={[dayISO]} createdById={drill.employeeId}
            clients={clients} timeCodes={timeCodes} minRows={0} review />
        ) : (
          <WeekView weekStart={drillWeekStarts[0]} createdById={drill.employeeId}
            clients={clients} timeCodes={timeCodes} minRows={0} review
            showEmptyDays={showEmptyDays} />
        )}
      </div>
    );
  }

  const employees = queue?.employees || [];

  return (
    <div style={{ padding: "16px 24px" }}>
      <div style={{ display: "flex", gap: 8, borderBottom: "1px solid #e5e7eb", marginBottom: 12 }}>
        {[["pending", "Awaiting approval"], ["approved", "Approved"]].map(([key, label]) => (
          <button key={key} onClick={() => setTab(key)}
            style={{ padding: "8px 16px", border: "none", cursor: "pointer", fontWeight: 600,
              borderBottom: tab === key ? "3px solid #2563eb" : "3px solid transparent",
              background: "transparent", color: tab === key ? "#2563eb" : "#374151" }}>
            {label}
          </button>
        ))}
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
        <button style={btn(mode === "day")} onClick={() => setMode("day")}>Day</button>
        <button style={btn(mode === "week")} onClick={() => setMode("week")}>Week</button>
        <button style={btn(mode === "month")} onClick={() => setMode("month")}>Month</button>
        <button onClick={() => stepPeriod(-1)}>‹ Prev</button>
        <strong style={{ minWidth: 220, textAlign: "center" }}>{periodLabel(mode, anchor)}</strong>
        <button onClick={() => stepPeriod(1)}>Next ›</button>
        {tab === "pending" && (
          <button
            onClick={onApproveAll}
            disabled={busy || !queue?.entry_count}
            style={{ marginLeft: "auto", padding: "8px 16px", borderRadius: 8, border: "none",
              fontWeight: 700, cursor: queue?.entry_count ? "pointer" : "not-allowed",
              background: queue?.entry_count ? "#16a34a" : "#e5e7eb",
              color: queue?.entry_count ? "white" : "#9ca3af" }}>
            {busy ? "Approving…" : `Approve entire ${mode} (all employees)`}
          </button>
        )}
      </div>

      {banner && (
        <div style={{ padding: "8px 12px", borderRadius: 8, marginBottom: 12, fontWeight: 600,
          background: banner.type === "ok" ? "#dcfce7" : "#fee2e2",
          color: banner.type === "ok" ? "#166534" : "#991b1b" }}>
          {banner.msg}
        </div>
      )}

      {loading ? (
        <div style={{ padding: 24, color: "#6b7280" }}>Loading…</div>
      ) : queueError ? (
        <div style={{ padding: 24, color: "#991b1b", fontWeight: 600 }}>{queueError}</div>
      ) : employees.length === 0 ? (
        <div style={{ padding: 24, color: "#6b7280" }}>
          {tab === "pending"
            ? "Nothing awaiting approval for this period."
            : "No approved time for this period."}
        </div>
      ) : (
        <table style={{ width: "100%", borderCollapse: "collapse", background: "white",
          border: "1px solid #e5e7eb", borderRadius: 10, overflow: "hidden" }}>
          <thead>
            <tr style={{ background: "#f9fafb", textAlign: "left" }}>
              <th style={{ padding: "10px 12px", fontSize: 12, color: "#6b7280" }}>Employee</th>
              <th style={{ padding: "10px 12px", fontSize: 12, color: "#6b7280" }}>Weeks</th>
              <th style={{ padding: "10px 12px", fontSize: 12, color: "#6b7280", textAlign: "right" }}>Entries</th>
              <th style={{ padding: "10px 12px", fontSize: 12, color: "#6b7280", textAlign: "right" }}>Hours</th>
              <th style={{ padding: "10px 12px" }}></th>
            </tr>
          </thead>
          <tbody>
            {employees.map((emp) => (
              <tr key={emp.employee_id} style={{ borderTop: "1px solid #f3f4f6" }}>
                <td style={{ padding: "10px 12px", fontWeight: 600 }}>{emp.name}</td>
                <td style={{ padding: "10px 12px", color: "#6b7280", fontSize: 13 }}>
                  {emp.weeks.map((w) => w.week_start).join(", ")}
                </td>
                <td style={{ padding: "10px 12px", textAlign: "right" }}>{emp.entry_count}</td>
                <td style={{ padding: "10px 12px", textAlign: "right", fontWeight: 600 }}>
                  {Number(emp.total_hours).toFixed(2)}
                </td>
                <td style={{ padding: "10px 12px", textAlign: "right" }}>
                  <button
                    style={btn(false)}
                    onClick={() => {
                      // The drill-down gets its own anchor and day, seeded from this
                      // employee's first pending week — the console's own mode/anchor
                      // (queue + bulk-approve range) are left untouched (Fix 3).
                      const firstWeek = emp.weeks[0]?.week_start || anchor;
                      setDrill({ employeeId: emp.employee_id, name: emp.name });
                      setGrain("week");
                      setDrillAnchor(firstWeek);
                      setDayISO(firstWeek);
                    }}>
                    Review
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr style={{ background: "#f9fafb", borderTop: "1px solid #e5e7eb" }}>
              <td style={{ padding: "10px 12px", fontWeight: 700 }} colSpan={2}>Total</td>
              <td style={{ padding: "10px 12px", textAlign: "right", fontWeight: 700 }}>
                {queue?.entry_count ?? 0}
              </td>
              <td style={{ padding: "10px 12px", textAlign: "right", fontWeight: 700 }}>
                {Number(queue?.total_hours || 0).toFixed(2)}
              </td>
              <td></td>
            </tr>
          </tfoot>
        </table>
      )}
    </div>
  );
}
